import $ from 'jquery';
import { getPrettyJson } from '../util/scriptUtils';

export class ScriptSchema {
    static DEFAULT_JOB_SCHEMA = `{
    "jobs": [{
        "job_reference": "Name of the job",
        "url": "URL that opened to execute this job",
        "input": {
            "attribute_1": "Parameters to pass to the job",
            "attribute_n": "Parameters to pass to the job"
        },
        "data": {
            "attribute_1": "Data extracted from the page",
            "attribute_n": "Data extracted from the page"
        }
    }]
}`;
    static DEFAULT_DATA_SCHEMA = `{ 
    "data": {
        "output": [{
            "reference_id": "A reference for this data, unique within the extraction", 
            "attribute_1": "Data extracted from the page",
            "attribute_n": "Data extracted from the page"
        }]
    }
}`;

    constructor(job = ScriptSchema.DEFAULT_JOB_SCHEMA, data = ScriptSchema.DEFAULT_DATA_SCHEMA, selectedSchema = "Job") {
        this.job = job;
        this.data = data;
        this.selectedSchema = selectedSchema;
    }

    toJsonString() {
        // use this approach rather than JSON.stringify to avoid applying stringfy to the job and data
        // which are already in string format
        return `{
            "job": ${this.job},
            "data": ${this.data},
            "selectedSchema": "${this.selectedSchema}"
        }`;
    }

    static fromJsonString(jsonString) {
        // if there's no stored version of the schema, return the default one
        if (jsonString == null || jsonString.trim() == "") {
            return new ScriptSchema();
        }
        const obj = JSON.parse(jsonString);
        const schema = new ScriptSchema(
            JSON.stringify(obj.job, null, 4), JSON.stringify(obj.data, null, 4), obj.selectedSchema
        );
        return schema;
    }
}

export class JobDefinitionService {

    static PLAYWRIGHT_RENDER_TYPE = 2;
    static SITEMAP_RENDER_TYPE = 3;
    static MINIMIZER_JOB_DEFINITION_ID = 26;
    static DEFAULT_SCRIPT_SCHEMA = new ScriptSchema();
    
    constructor(component) {
        this.component = component;
    }

    loadJobDefinition(extractionId, jobDefinitionId) {
        return new Promise((resolve, reject) => {
            try {
                $.get("/api/v1/extractions/" + extractionId + "/job_definitions/" + jobDefinitionId)
                    .then(res => {
                        var debugText = " ";
                        if (res.last_test_json && res.last_test_json.trim() !== "") {
                            debugText = getPrettyJson(res.last_test_json);
                        }
                        resolve({ jobDefinition: res, debugText: debugText });
                    })
                    .catch(error => {
                        reject(error);
                    });
            } catch (error) {
                reject(error);
            }
        });
    }

    updateJobDefinition(jobDefinition) {
        return new Promise((resolve, reject) => {
            try {
                let jobDefinitionToUpdate = { ...jobDefinition, script_generation_requests: [] };
                // use ajax function to overide the default 400 behavior defined in main.jsx
                $.ajax({
                    url: "/api/v1/extractions/" + jobDefinition.extraction_id + "/job_definitions",
                    method: 'PUT',
                    data: JSON.stringify(jobDefinitionToUpdate),
                    contentType: 'application/json',
                    success: (res, textStatus, xhr) => {
                        const status = xhr.status === 201 ? "created" : "updated";
                        resolve({ jobDefinition: res, status: status, errorMessage: null });
                    },
                    statusCode: {
                        // overide the default 400 behavior defined in main.jsx
                        400: (xhr) => {
                            let errorMessage = 'Failed to create job definition';
                            if (xhr.responseText) {
                                try {
                                    const response = JSON.parse(xhr.responseText);
                                    if (response.message) {
                                        errorMessage = response.message;
                                    }
                                } catch (e) {
                                    console.log('Error parsing server response:', e);
                                }
                            }
                            resolve({ jobDefinition: null, status: null, errorMessage: errorMessage });
                        }
                    }
                }).catch(error => {
                    reject(error);
                });
            } catch (error) {
                reject(error);
            }
        });
    }

    updateAllJobDefinitionsForExtraction(extractionId, jobDefinitionIdToSkip = -1) {
        return new Promise((resolve, reject) => {
            try {
                const extraction = this.component.state.extractions.find(e => e.id == extractionId);
                if (!extraction) {
                    reject(new Error("Extraction not found"));
                }
                const updatePromises = extraction.job_definitions.map(jobDefinition => {
                    if (jobDefinition.id == jobDefinitionIdToSkip) {
                        return null;
                    }
                    return this.updateJobDefinition(jobDefinition)
                        .then(() => this.component.stateManagementService.updateJobDefinition(jobDefinition));
                }).filter(promise => promise !== null);
                Promise.all(updatePromises)
                    .then(() => resolve())
                    .catch(error => reject(error));
            } catch (error) {
                reject(error);
            }
        });
    }

    deleteJobDefinition(extractionId, jobDefinitionId) {
        return new Promise((resolve, reject) => {
            try {
                $.delete("/api/v1/extractions/" + extractionId + "/job_definitions/" + jobDefinitionId, res => {
                    resolve(res);
                });
            } catch (error) {
                reject(error);
            }
        });
    }

    isJobReferenceUnique(jobId, jobReference) {
        const extraction = this.component.state.extractions?.find(e => e.id == this.component.state.currentExtractionId);
        const jobDefinitions = extraction ? extraction.job_definitions : [];
        const jobDefinition = jobDefinitions.find(jobDefinition => jobDefinition.job_reference == jobReference);
        // If no job definition has the same reference, or one does but it's the current job definition, then the reference is unique
        return jobDefinition == null || jobDefinition.id == jobId;
    }   

    publishJob(extractionId, jobDefinitionId, jobConfiguration) {
        return new Promise((resolve, reject) => {
            try {
                $.post("/api/v1/extractions/" + extractionId + "/job_definitions/" + jobDefinitionId + "/crawl/start", JSON.stringify(jobConfiguration), publishedBatch => {
                    resolve(publishedBatch);
                })
                .catch(error => {
                    reject(error);
                });
            } catch (error) {
                reject(error);
            }
        });
    }

    getCrawlerOutput(extractionId, batchId, jobId) {
        return new Promise((resolve, reject) => {
            try {
                $.get("/api/v1/extractions/" + extractionId + "/batches/" + batchId + "/jobs/" + encodeURIComponent(jobId), res => {
                    if (res != null) {
                        let output = ""
                        // default to the info attribute, which will hold information about any errors that occurred
                        let output_attribute = "info";
                        // if the output attribute is not null, then the job was successful and this will hold the output
                        if (res["output"] && res["output"] != "null") {
                            output_attribute = "output"
                        }
                        try {
                            let obj = JSON.parse(res[output_attribute])
                            output += JSON.stringify(obj, null, 2)
                        }
                        catch (err) {
                            output += res[output_attribute]
                        }
                        resolve({ pureOutput: res, processedOutput: output });
                    }
                    resolve(null);
                })
                .catch(error => {
                    reject(error);
                });
            } catch (error) {
                reject(error);
            }
        });
    }

    // isValidURL(siteUrl) {
  //   domain = this.extractDomain(siteUrl);
  //   if (domain != null) {
  //     return true;
  //   }
  // }

  // getStartUrlValidationState() {
  //   var job_definition = this.state.job_definition
  //   if (job_definition != null) {
  //       // Check the URL, if not primary and no URL set, skip the check since secondary job definitions don't need a URL
  //       if (job_definition.root_job || job_definition.start_url) {
  //           // extract the domain component of the URL
  //           var extractedDomain = extractDomain(job_definition.start_url);
  //           if (extractedDomain != null) {
  //               // get a substring of x characters from the end of the extracted domain where
  //               // x matches the number of characters in the target extractions permitted domain
  //               for (var i = 0; i < this.state.extractionDomains.length; i++) {
  //                   var domain = extractedDomain
  //                   // TODO: check if the extractionDomain is a regex, if so match the regex
  //                   // rather than the domain string
  //                   if (domain.length >= this.state.extractionDomains[i].domain.length) {
  //                       domain = domain.substring(
  //                           domain.length - this.state.extractionDomains[i].domain.length,
  //                           domain.length);
  //                   }
  //                   // if the domain for the job's start URL doesn't end with the permitted extraction domain,
  //                   // the provided start URL isn't a match or sub domain for the extraction it's
  //                   // supposed to crawl and is therefore invalid. Likewise if the start URL isn't a valid
  //                   // URL at all
  //                   if ((domain == this.state.extractionDomains[i].domain)
  //                       && (this.isValidURL(job_definition.start_url))) {
  //                       this.state.jobStartURLValid = true;
  //                       return null;
  //                   }
  //               }
  //               this.state.jobStartURLValid = false;
  //               return 'error';
  //           }
  //       }
  //   }
  //   this.state.jobStartURLValid = true;
  //   return null;
  // }  
}

